import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Music2, Plus, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { isYouTubeUrl, formatDuration } from '../utils/roomHelpers'
import { useKeyboardHeight } from '../hooks/useKeyboardHeight'
import useSheetDragDismiss from '../hooks/useSheetDragDismiss'
import { cn } from '../lib/utils'
import { Card } from './ui'
import { SkeletonList, SkeletonSearchCard } from './ui/Skeleton'
import { EmptySearchState } from './ui/EmptyState'
import {
  CATALOG_MATCH_MIN_QUERY_LEN,
  CATALOG_MATCH_WEAK_THRESHOLD,
  catalogTopMatchScore
} from '../utils/catalogMatchScore'

/** Matches backend / roomHelpers URL patterns for processing job rows (jobs store `youtubeUrl`, not id). */
const youtubeIdFromWatchUrl = (url) => {
  if (!url) return null
  return url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1] ?? null
}

const YOUTUBE_JOB_ACTIVE_STATUSES = new Set(['pending', 'queued', 'processing'])

const decodeHtmlEntities = (text) => {
  const textArea = document.createElement('textarea')
  textArea.innerHTML = text
  return textArea.value
}

const ResultCard = memo(({ title, subtitle, duration, badge, thumbnailUrl, action, actionLabel, actionDisabled = false, buttonText = 'Add', buttonPending = false }) => {
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false)

  useEffect(() => {
    if (!thumbnailUrl) {
      setThumbnailLoaded(false)
      return
    }

    const img = new Image()
    img.onload = () => setThumbnailLoaded(true)
    img.onerror = () => setThumbnailLoaded(false)
    img.src = thumbnailUrl
  }, [thumbnailUrl])

  return (
    <Card className="relative overflow-hidden rounded-2xl border border-white/15 bg-[color:rgba(16,27,45,0.88)] p-0">
      {thumbnailLoaded ? (
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(100deg, rgba(3,9,18,0.9), rgba(3,9,18,0.42)), url(${thumbnailUrl})`
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-[linear-gradient(145deg,#f97316_0%,#fb7185_50%,#7f1d1d_100%)] opacity-30" />
      )}

      <div className="relative z-10 flex min-h-[88px] items-center justify-between gap-3 px-3 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="truncate text-sm font-semibold text-[var(--room-text)]">{title}</h4>
            {badge ? (
              <span className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--room-muted)]">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="truncate text-xs text-[var(--room-muted)]">{subtitle}</p>
          {duration ? <p className="mt-1 text-[11px] text-[var(--room-muted)]">{duration}</p> : null}
        </div>

        <button
          type="button"
          onClick={action}
          disabled={actionDisabled}
          className={cn(
            'room-focus-ring inline-flex h-11 min-w-[70px] items-center justify-center gap-1 rounded-xl border px-2 text-xs font-semibold uppercase tracking-[0.04em] transition-colors',
            actionDisabled
              ? 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
              : 'border-[color:var(--room-primary)] bg-[color:rgba(46,168,255,0.2)] text-[var(--room-text)] hover:bg-[color:rgba(46,168,255,0.28)]'
          )}
          aria-label={actionLabel}
          title={actionLabel}
          aria-busy={buttonPending || undefined}
        >
          {buttonPending ? (
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Plus className="h-3.5 w-3.5" aria-hidden />
          )}
          {buttonText}
        </button>
      </div>
    </Card>
  )
})

ResultCard.displayName = 'ResultCard'

const DatabaseSearchCard = memo(({ song, processingJob, isAdded, handleAddToQueue }) => {
  const thumbnailUrl = song.youtubeId ? `/uploads/songs/${song.youtubeId}/thumbnail.webp` : null
  const isProcessingCompleted = processingJob && processingJob.status === 'completed'
  const isProcessing = processingJob && processingJob.status !== 'completed'
  const actionLabel = isAdded ? `Add ${song.title} again to queue` : `Add ${song.title} to queue`

  return (
    <ResultCard
      title={song.title}
      subtitle={song.artist}
      duration={formatDuration(song.duration)}
      badge={isProcessing ? 'Processing' : (isProcessingCompleted ? 'Ready' : 'Library')}
      thumbnailUrl={thumbnailUrl}
      action={() => handleAddToQueue(song)}
      actionLabel={actionLabel}
      actionDisabled={isProcessing}
      buttonText={isAdded ? 'Add Again' : 'Add'}
    />
  )
})

DatabaseSearchCard.displayName = 'DatabaseSearchCard'

const YouTubeSearchCard = memo(({ video, isSubmitting, isPendingSubmit, hasConvexActiveJob, isAdded, handleAddYouTubeVideo }) => {
  const title = decodeHtmlEntities(video.title)
  const artist = decodeHtmlEntities(video.artist)
  const apiActive = video.processingStatus && YOUTUBE_JOB_ACTIVE_STATUSES.has(video.processingStatus)
  const jobInProgress = !video.alreadyExists && (apiActive || hasConvexActiveJob)
  const blocked = isSubmitting || isPendingSubmit || jobInProgress

  const actionLabel = isPendingSubmit
    ? `Adding ${title} to processing queue`
    : isAdded
      ? `Add ${title} again`
      : (video.alreadyExists ? `Add ${title} from library` : `Queue ${title} from YouTube`)

  let badge
  if (video.alreadyExists) {
    badge = 'In Library'
  } else if (isPendingSubmit) {
    badge = 'Adding'
  } else if (jobInProgress) {
    badge = 'Processing'
  } else {
    badge = 'YouTube'
  }

  const buttonText = isPendingSubmit
    ? 'Adding…'
    : (isAdded ? 'Add Again' : 'Add')

  return (
    <ResultCard
      title={title}
      subtitle={`${artist} • YouTube`}
      duration={formatDuration(video.duration)}
      badge={badge}
      thumbnailUrl={video.thumbnailUrl}
      action={() => handleAddYouTubeVideo(video)}
      actionLabel={actionLabel}
      actionDisabled={blocked}
      buttonText={buttonText}
      buttonPending={isPendingSubmit}
    />
  )
})

YouTubeSearchCard.displayName = 'YouTubeSearchCard'

const SearchModal = ({
  inputQuery,
  setInputQuery,
  isSearchModalOpen,
  isSearchModalAnimating,
  closeSearchModal,
  isSubmitting,
  handleSubmitInput,
  handleSearchSubmit,
  // Search results
  isSearching,
  searchResults,
  handleAddToQueue,
  queue = [],
  processingJobs,
  sessionId,
  // YouTube results
  isSearchingYouTube,
  youtubeSearchResults,
  hasSearchedYouTube,
  submitYouTubeUrl
}) => {
  const inputRef = useRef(null)
  const panelRef = useRef(null)
  const [manuallyAddedSongIds, setManuallyAddedSongIds] = useState(() => new Set())
  const [manuallyAddedYoutubeIds, setManuallyAddedYoutubeIds] = useState(() => new Set())
  const [pendingYoutubeSubmitIds, setPendingYoutubeSubmitIds] = useState(() => new Set())

  const convexActiveYoutubeIds = useMemo(() => {
    const next = new Set()
    const jobsMap = processingJobs instanceof Map ? processingJobs : new Map()
    for (const job of jobsMap.values()) {
      if (!YOUTUBE_JOB_ACTIVE_STATUSES.has(job.status)) continue
      const id = youtubeIdFromWatchUrl(job.youtubeUrl)
      if (id) next.add(id)
    }
    return next
  }, [processingJobs])

  const queuedSongIds = useMemo(() => {
    const ids = (queue || [])
      .map((item) => (item?.songId !== undefined && item?.songId !== null ? String(item.songId) : null))
      .filter(Boolean)
    return new Set(ids)
  }, [queue])

  const queuedYoutubeIds = useMemo(() => {
    const ids = (queue || [])
      .map((item) => item?.song?.youtubeId || null)
      .filter(Boolean)
    return new Set(ids)
  }, [queue])

  useEffect(() => {
    if (isSearchModalOpen && isSearchModalAnimating && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 120)
    }
  }, [isSearchModalOpen, isSearchModalAnimating])

  useKeyboardHeight()

  const {
    dragHandleProps,
    dragOffset,
    isDragging,
    resetDrag
  } = useSheetDragDismiss({
    enabled: isSearchModalOpen && isSearchModalAnimating,
    onDismiss: closeSearchModal
  })

  useEffect(() => {
    if (!isSearchModalAnimating) {
      resetDrag()
    }
  }, [isSearchModalAnimating, resetDrag])

  const showWeakMatchYoutubeCta = useMemo(() => {
    const q = inputQuery.trim()
    if (!q || isYouTubeUrl(inputQuery)) return false
    if (isSearching || searchResults.length === 0 || hasSearchedYouTube) return false
    if (q.length < CATALOG_MATCH_MIN_QUERY_LEN) return false
    const top = searchResults[0]
    return catalogTopMatchScore(inputQuery, top) < CATALOG_MATCH_WEAK_THRESHOLD
  }, [inputQuery, isSearching, searchResults, hasSearchedYouTube])

  /** Bottom-of-list YouTube CTA: weak match uses emphasis; otherwise subtle nudge after scrolling catalog. */
  const showBottomYoutubeSearchCta = useMemo(() => {
    const q = inputQuery.trim()
    if (!q || isYouTubeUrl(inputQuery)) return false
    if (isSearching || searchResults.length === 0 || hasSearchedYouTube) return false
    if (q.length < CATALOG_MATCH_MIN_QUERY_LEN) return false
    return true
  }, [inputQuery, isSearching, searchResults, hasSearchedYouTube])

  const markSongAsAdded = (songId, youtubeId = null) => {
    if (songId !== undefined && songId !== null) {
      const normalizedSongId = String(songId)
      setManuallyAddedSongIds((current) => {
        const updated = new Set(current)
        updated.add(normalizedSongId)
        return updated
      })
    }

    if (youtubeId) {
      setManuallyAddedYoutubeIds((current) => {
        const updated = new Set(current)
        updated.add(youtubeId)
        return updated
      })
    }
  }

  const handleAddDatabaseSong = async (song) => {
    try {
      await handleAddToQueue(song.id)
      markSongAsAdded(song.id, song.youtubeId || null)
    } catch {
      // Parent handler owns user-facing error handling.
    }
  }

  const handleAddYouTubeVideo = async (video) => {
    const ytId = video.youtubeId
    if (!ytId) return

    const toastId = `yt-queue-${ytId}`
    setPendingYoutubeSubmitIds((current) => {
      const next = new Set(current)
      next.add(ytId)
      return next
    })
    toast.loading('Adding to processing queue…', { id: toastId })

    try {
      await submitYouTubeUrl(video.youtubeUrl, sessionId)
      markSongAsAdded(video.songId || null, ytId)
    } catch (error) {
      console.error('Failed to submit YouTube URL:', error)
      const status = error.response?.status
      const detail = error.response?.data?.detail || error.message || ''

      if (status === 409) {
        toast.error(detail.toLowerCase().includes('already exists') ? 'Song already added to library' : (detail || 'Song already exists'))
      } else if (status === 429) {
        toast.error(detail || 'Rate limit exceeded')
      } else if (error.isNetworkError) {
        toast.error('Network error - please check your connection')
      } else if (detail) {
        toast.error(detail)
      } else {
        toast.error('Failed to submit song. Please try again.')
      }
    } finally {
      toast.dismiss(toastId)
      setPendingYoutubeSubmitIds((current) => {
        const next = new Set(current)
        next.delete(ytId)
        return next
      })
    }
  }

  const renderSearchResults = () => {
    if (isSearching) {
      return <SkeletonList count={3} component={SkeletonSearchCard} isDarkMode />
    }

    if (!inputQuery.trim() || isYouTubeUrl(inputQuery)) {
      return null
    }

    if (searchResults.length === 0) {
      return (
        <EmptySearchState
          isDarkMode
          query={inputQuery}
          onYouTubeSearch={handleSearchSubmit}
          isSearchingYouTube={isSearchingYouTube}
          hasSearchedYouTube={hasSearchedYouTube}
        />
      )
    }

    const youtubeFooterButton = (emphasis, ariaLabel) => (
      <button
        type="button"
        onClick={() => handleSearchSubmit()}
        disabled={isSearchingYouTube}
        className={cn(
          'room-focus-ring inline-flex min-h-11 w-full items-center justify-center rounded-xl border px-3 text-xs font-semibold uppercase tracking-[0.04em] transition-colors',
          isSearchingYouTube
            ? 'cursor-not-allowed border-white/10 bg-black/20 text-white/35'
            : emphasis
              ? 'border-amber-400/50 bg-amber-500/20 text-[var(--room-text)] hover:bg-amber-500/30'
              : 'border-white/20 bg-white/10 text-[var(--room-text)] hover:bg-white/15'
        )}
        aria-label={ariaLabel}
      >
        {isSearchingYouTube ? 'Searching YouTube…' : 'Search YouTube'}
      </button>
    )

    return (
      <div className="space-y-3">
        {showWeakMatchYoutubeCta ? (
          <div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 px-3 py-3 text-center">
            <p className="mb-2 text-xs text-[var(--room-muted)]">Not finding what you meant?</p>
            {youtubeFooterButton(true, 'Search YouTube for this query')}
          </div>
        ) : null}
        {searchResults.map((song) => {
          const processingJob = Array.from(processingJobs.values()).find(
            (job) => job.song?.id === song.id && job.addedBy === sessionId
          )
          const songKey = String(song.id)
          const isAdded = manuallyAddedSongIds.has(songKey)
            || queuedSongIds.has(songKey)
            || (song.youtubeId ? manuallyAddedYoutubeIds.has(song.youtubeId) || queuedYoutubeIds.has(song.youtubeId) : false)

          return (
            <DatabaseSearchCard
              key={song.id}
              song={song}
              processingJob={processingJob}
              isAdded={isAdded}
              handleAddToQueue={handleAddDatabaseSong}
            />
          )
        })}
        {showBottomYoutubeSearchCta && !showWeakMatchYoutubeCta ? (
          <div className="rounded-2xl border border-white/15 bg-white/5 px-3 py-3 text-center">
            <p className="mb-2 text-xs text-[var(--room-muted)]">Looking for something else?</p>
            {youtubeFooterButton(false, 'Search YouTube for other songs')}
          </div>
        ) : null}
      </div>
    )
  }

  const renderYouTubeSearchResults = () => {
    if (isSearchingYouTube) {
      return <SkeletonList count={3} component={SkeletonSearchCard} isDarkMode />
    }

    if (youtubeSearchResults.length === 0 && hasSearchedYouTube) {
      return (
        <EmptySearchState
          isDarkMode
          query={inputQuery}
          onYouTubeSearch={handleSearchSubmit}
          isSearchingYouTube={isSearchingYouTube}
          hasSearchedYouTube={hasSearchedYouTube}
        />
      )
    }

    return (
      <div className="space-y-3">
        {youtubeSearchResults.map((video) => (
          <YouTubeSearchCard
            key={video.youtubeId}
            video={video}
            isSubmitting={isSubmitting}
            isPendingSubmit={pendingYoutubeSubmitIds.has(video.youtubeId)}
            hasConvexActiveJob={convexActiveYoutubeIds.has(video.youtubeId)}
            isAdded={manuallyAddedYoutubeIds.has(video.youtubeId) || queuedYoutubeIds.has(video.youtubeId)}
            handleAddYouTubeVideo={handleAddYouTubeVideo}
          />
        ))}
      </div>
    )
  }

  if (!isSearchModalOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[62]">
      <button
        type="button"
        onClick={closeSearchModal}
        aria-label="Close search sheet"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />

      <div className="absolute inset-x-0 bottom-0">
        <div
          ref={panelRef}
          className={cn(
            'room-elevated mx-auto h-[82vh] w-full max-w-lg rounded-t-3xl border-b-0 shadow-[0_-16px_42px_rgba(2,8,18,0.58)] transition-transform duration-300 ease-out'
          )}
          style={{
            transform: isSearchModalAnimating ? `translateY(${dragOffset}px)` : 'translateY(100%)',
            transitionDuration: isDragging ? '0ms' : '300ms'
          }}
        >
          <div className="flex h-full flex-col">
            <div className="room-content-safe-inline sticky top-0 z-10 rounded-t-3xl border-b border-white/10 bg-[color:rgba(16,27,45,0.96)] pb-3 pt-3">
              <button
                type="button"
                aria-label="Drag to dismiss search sheet"
                className="mx-auto mb-3 flex h-6 w-16 touch-none items-center justify-center rounded-full"
                style={{ touchAction: 'none' }}
                {...dragHandleProps}
              >
                <span className="h-1.5 w-12 rounded-full bg-white/20" />
              </button>
              <div className="mb-2">
                <h3 className="text-sm font-semibold uppercase tracking-[0.06em] text-[var(--room-muted)]">Search</h3>
              </div>

              <form onSubmit={handleSubmitInput}>
                <div className="room-input flex h-12 items-center gap-2 rounded-xl px-3">
                  <Search className="h-4 w-4 text-[var(--room-muted)]" />
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search songs or paste YouTube URL"
                    value={inputQuery}
                    onChange={(event) => setInputQuery(event.target.value)}
                    className="room-focus-ring w-full bg-transparent text-sm outline-none placeholder:text-[var(--room-muted)]"
                    style={{ fontSize: '16px' }}
                    autoFocus
                  />
                </div>
              </form>
            </div>

            <div className="room-content-safe-inline scrollbar-style flex-1 overflow-y-auto pb-24 pt-3">
              <div className="space-y-5">
                {!isYouTubeUrl(inputQuery) && (isSearching || searchResults.length > 0) ? (
                  <section>
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--room-muted)]">
                      <Music2 className="h-3.5 w-3.5" />
                      Available Songs
                    </div>
                    {renderSearchResults()}
                  </section>
                ) : null}

                {(isSearchingYouTube || youtubeSearchResults.length > 0) ? (
                  <section>
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--room-muted)]">
                      <Search className="h-3.5 w-3.5" />
                      YouTube Results
                    </div>
                    {renderYouTubeSearchResults()}
                  </section>
                ) : null}

                {!isSearching && !isSearchingYouTube && searchResults.length === 0 && youtubeSearchResults.length === 0 && inputQuery.trim() && !isYouTubeUrl(inputQuery) ? (
                  <EmptySearchState
                    isDarkMode
                    query={inputQuery}
                    onYouTubeSearch={handleSearchSubmit}
                    isSearchingYouTube={isSearchingYouTube}
                    hasSearchedYouTube={hasSearchedYouTube}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchModal
